import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useHousehold } from "../auth/AuthProvider";
import { del, hpath } from "../lib/api";
import { keys, useTimers } from "../lib/queries";
import type { Timer } from "../lib/types";

/** Ticking clock, only while something needs it. */
export function useNow(active: boolean, intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
  return now;
}

/** Deadline-based remaining seconds: computed from endsAt so refresh/reopen never drifts. */
export function remainingSeconds(timer: Timer, now: number): number {
  if (timer.status === "paused") return Math.max(0, timer.remainingSeconds ?? 0);
  if (timer.status === "done" || !timer.endsAt) return 0;
  return Math.max(0, Math.ceil((Date.parse(timer.endsAt) - now) / 1000));
}

export function effectiveStatus(timer: Timer, now: number): Timer["status"] {
  if (timer.status === "running" && timer.endsAt && Date.parse(timer.endsAt) <= now) return "done";
  return timer.status;
}

export function formatClock(total: number): string {
  const s = Math.max(0, Math.round(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0");
  return h ? `${h}:${mm}:${String(sec).padStart(2, "0")}` : `${mm}:${String(sec).padStart(2, "0")}`;
}

export function spokenDuration(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [h ? `${h} hour${h === 1 ? "" : "s"}` : "", m ? `${m} minute${m === 1 ? "" : "s"}` : "", s && !h ? `${s} second${s === 1 ? "" : "s"}` : ""].filter(Boolean);
  return parts.join(" ") || "0 seconds";
}

/* ---------------- Chime (Web Audio, unlocked by a user gesture) ---------------- */

let audioCtx: AudioContext | null = null;

export function unlockAudio() {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audioCtx ??= new Ctor();
    if (audioCtx.state === "suspended") void audioCtx.resume();
  } catch {
    // audio unavailable
  }
}

export function playChime() {
  try {
    if (!audioCtx) unlockAudio();
    const ctx = audioCtx;
    if (!ctx) return;
    const start = ctx.currentTime + 0.02;
    // Three bright enamel-bell strikes.
    [0, 0.42, 0.84].forEach((offset, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = i === 2 ? 1318.5 : 1046.5;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.28, start + offset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.38);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + offset);
      osc.stop(start + offset + 0.4);
    });
  } catch {
    // ignore
  }
  try {
    navigator.vibrate?.([200, 100, 200, 100, 300]);
  } catch {
    // ignore
  }
}

const ALERTED_KEY = "sk:timers-alerted";
function loadAlerted(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(ALERTED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
function saveAlerted(set: Set<string>) {
  try {
    sessionStorage.setItem(ALERTED_KEY, JSON.stringify([...set].slice(-100)));
  } catch {
    // ignore
  }
}

/**
 * App-wide timer watcher: rings (sound + vibration + toast) when any of the user's timers reaches
 * its deadline, wherever the user is in the app.
 */
export function TimerWatcher() {
  const { householdId } = useHousehold();
  const queryClient = useQueryClient();
  const timers = useTimers();
  const running = (timers.data ?? []).filter((t) => t.status === "running");
  const now = useNow(running.length > 0, 1000);
  const alerted = useRef<Set<string>>(loadAlerted());

  useEffect(() => {
    for (const timer of timers.data ?? []) {
      if (timer.status === "running" && effectiveStatus(timer, now) === "done" && !alerted.current.has(timer.id)) {
        alerted.current.add(timer.id);
        saveAlerted(alerted.current);
        playChime();
        toast.warning(`${timer.label} — time's up`, {
          id: `timer-${timer.id}`,
          duration: Infinity,
          action: {
            label: "Dismiss",
            onClick: () => {
              void del(hpath(householdId, `/timers/${timer.id}`)).then(() => queryClient.invalidateQueries({ queryKey: keys.timers(householdId) }));
            },
          },
        });
      }
      if (timer.status !== "running" && alerted.current.has(timer.id) && timer.status !== "done") {
        // Restarted/resumed later: allow a new alert.
        alerted.current.delete(timer.id);
        saveAlerted(alerted.current);
      }
    }
  }, [timers.data, now, householdId, queryClient]);

  return null;
}
